import { Link } from 'react-router-dom';
import type { RosterUploadListItem } from '../../../types/adminRosterUploads';
import { RosterUploadStatusBadge } from './RosterUploadStatusBadge';
import {
  EMPTY_CELL,
  filesLabel,
  formatCount,
  formatDuration,
  formatInstant,
  formatRelative,
  sourceLabel,
  summarizeTeams,
} from './rosterUploadFormat';
import { ROSTER_UPLOADS_PATH, rosterUploadsHref } from './rosterUploadsNavigation';

interface RosterUploadsTableProps {
  items: readonly RosterUploadListItem[];
  now: Date;
  /** The list's own URL, handed to the detail view so its back link restores these filters. */
  backTo: string;
  emptyMessage: string;
}

const COLUMN_COUNT = 10;

const UploaderCell = ({ uploader }: { uploader: RosterUploadListItem['uploadedBy'] }) => {
  if (!uploader) return <span className="data-table__muted">Unknown</span>;
  return (
    <div className="roster-uploads__stack">
      <span>{uploader.name ?? uploader.email}</span>
      {uploader.name && <span className="roster-uploads__secondary">{uploader.email}</span>}
    </div>
  );
};

const TeamsCell = ({ teamNames }: { teamNames: readonly string[] }) => {
  if (teamNames.length === 0) return <span className="data-table__muted">{EMPTY_CELL}</span>;
  const { visible, remaining } = summarizeTeams(teamNames);
  return (
    <span className="roster-uploads__teams" title={teamNames.join(', ')}>
      {visible.join(', ')}
      {remaining > 0 && (
        <span className="roster-uploads__more">
          {' '}
          +{remaining}
          <span className="roster-uploads__sr-only"> more</span>
        </span>
      )}
    </span>
  );
};

const FlagsCell = ({ item }: { item: RosterUploadListItem }) => {
  const markers: { key: string; label: string; tone: string }[] = [];
  if (item.hasError) markers.push({ key: 'error', label: 'Error', tone: 'danger' });
  if (item.validationFlagCount > 0) {
    markers.push({
      key: 'flags',
      label: `${item.validationFlagCount} ${item.validationFlagCount === 1 ? 'flag' : 'flags'}`,
      tone: 'warning',
    });
  }
  if (item.validationMadeCorrections) {
    markers.push({ key: 'corrected', label: 'Corrected', tone: 'neutral' });
  }
  if (markers.length === 0) return <span className="data-table__muted">{EMPTY_CELL}</span>;
  return (
    <span className="roster-uploads__markers">
      {markers.map((marker) => (
        <span key={marker.key} className={`roster-uploads__marker roster-uploads__marker--${marker.tone}`}>
          {marker.label}
        </span>
      ))}
    </span>
  );
};

/**
 * Newest first, in the server's order. Each row opens its upload through a real link in the first
 * cell; the organization name is a second link that narrows the list to that organization.
 */
export const RosterUploadsTable = ({ items, now, backTo, emptyMessage }: RosterUploadsTableProps) => (
  <div className="data-table-card">
    <div className="data-table-wrap">
      <table className="data-table roster-uploads__table">
        <thead>
          <tr>
            <th scope="col">Uploaded</th>
            <th scope="col">Organization</th>
            <th scope="col">Uploaded by</th>
            <th scope="col">Source</th>
            <th scope="col">Status</th>
            <th scope="col" className="roster-uploads__numeric">Players</th>
            <th scope="col">Teams</th>
            <th scope="col" className="roster-uploads__numeric">Processing</th>
            <th scope="col" className="roster-uploads__numeric">Tokens</th>
            <th scope="col">Flags</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={COLUMN_COUNT} className="data-table__empty" data-testid="roster-uploads-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr key={item.id} data-testid={`roster-upload-row-${item.id}`}>
                <td>
                  <Link
                    to={`${ROSTER_UPLOADS_PATH}/${item.id}`}
                    state={{ from: backTo }}
                    className="roster-uploads__row-link"
                  >
                    <time dateTime={item.createdAt} className="roster-uploads__stack">
                      <span className="roster-uploads__primary">{formatRelative(item.createdAt, now)}</span>
                      <span className="roster-uploads__secondary">{formatInstant(item.createdAt)}</span>
                    </time>
                    {/* A links list otherwise reads as bare timestamps with nothing to tell them apart. */}
                    <span className="roster-uploads__sr-only">
                      , open {item.organizationName || 'Unnamed organization'} upload
                    </span>
                  </Link>
                </td>
                <td>
                  <Link
                    to={rosterUploadsHref({ organizationId: item.organizationId })}
                    className="roster-uploads__org-link"
                  >
                    {item.organizationName || 'Unnamed organization'}
                    <span className="roster-uploads__sr-only">, show only this organization</span>
                  </Link>
                </td>
                <td>
                  <UploaderCell uploader={item.uploadedBy} />
                </td>
                <td>
                  <div className="roster-uploads__stack">
                    <span>{sourceLabel(item.sourceType)}</span>
                    <span className="roster-uploads__secondary">{filesLabel(item.fileCount)}</span>
                  </div>
                </td>
                <td>
                  <RosterUploadStatusBadge status={item.status} />
                </td>
                <td className="roster-uploads__numeric">{formatCount(item.playerCount)}</td>
                <td>
                  <TeamsCell teamNames={item.teamNames} />
                </td>
                <td className="roster-uploads__numeric">{formatDuration(item.processingTimeMs)}</td>
                <td className="roster-uploads__numeric">{formatCount(item.totalTokens)}</td>
                <td>
                  <FlagsCell item={item} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);
